import { getAnonymousId } from '../utils/anonymousId';
import { getUtmsForProfile, getClickIds } from '../attribution/utm-manager';
import {
	AFFILIATE_CLICK_ID_TYPE,
	readAffiliateClick,
} from '../attribution/affiliate';
import LOCAL_STORAGE_KEY from '../constants/localStorageKey';

// Shared acquisition touchpoint contract — must stay identical to heru-website.
// Public endpoint, NO Bearer token. Fire-and-forget: never block the UI, swallow
// every error.

const BASE = import.meta.env.VITE_NEW_GATEWAY_HOST || 'https://apis.heru.app';
const TOUCHPOINT_URL = `${BASE}/api/v1/heru-user-management-engine/acquisition/touchpoint`;
const STITCH_URL = `${BASE}/api/v1/heru-user-management-engine/acquisition/web/stitch`;
const STITCH_SESSION_KEY = 'heru_touchpoint_session_stitch';

const SOURCE_SYSTEM = 'webapp';

// First click id present wins, in this priority order (shared contract).
const CLICK_ID_PRIORITY = [
	'gclid', 'gbraid', 'wbraid', 'fbclid', 'ttclid', 'rdt_cid', 'msclkid',
] as const;

type TouchType = 'session_start' | 'signup';

interface TouchpointBody {
	anonymous_id: string;
	occurred_at: string;
	touch_type: TouchType;
	source_system: typeof SOURCE_SYSTEM;
	utm_source?: string;
	utm_medium?: string;
	utm_campaign?: string;
	utm_term?: string;
	utm_content?: string;
	click_id_type?: string;
	click_id_value?: string;
	referrer?: string;
	landing_page?: string;
}

/**
 * El clic al que se le atribuye este touchpoint.
 *
 * El de afiliado gana sobre los de plataformas de anuncios, y no por
 * importancia sino por FRESCURA: es el único que caduca (30 días), mientras que
 * un `gclid`/`fbclid` se guarda para siempre y jamás se limpia. Sin esta
 * precedencia, alguien que tocó un anuncio de Google hace un año y hoy entra
 * por el link del afiliado reportaría el `gclid` viejo — y ese clic es el que
 * factura comisión.
 *
 * La asimetría es conocida y va en la dirección barata: si el afiliado es el
 * viejo, ya venció y no compite. El arreglo de fondo es ponerle timestamp a
 * TODOS los click IDs, y eso es otro cambio.
 *
 * UME acepta cualquier string como `click_id_type` (Touchpoint.ClickIDType es
 * genérico), así que el tipo de afiliado no requiere cambio de backend. Ojo: su
 * `deriveChannel` todavía no conoce afiliados, así que hoy estos clics caen en
 * el canal 'Referral'.
 */
const resolveFirstClickId = (): { type: string; value: string } | null => {
	const affiliate = readAffiliateClick();
	if (affiliate) {
		return { type: AFFILIATE_CLICK_ID_TYPE, value: affiliate.clickId };
	}
	const clickIds = getClickIds();
	for (const key of CLICK_ID_PRIORITY) {
		const value = clickIds[key];
		if (value) return { type: key, value };
	}
	return null;
};

export const emitTouchpoint = (input: { touch_type: TouchType }): void => {
	try {
		if (typeof window === 'undefined') return;

		const clickId = resolveFirstClickId();

		const body: TouchpointBody = {
			anonymous_id: getAnonymousId(),
			occurred_at: new Date().toISOString(),
			touch_type: input.touch_type,
			source_system: SOURCE_SYSTEM,
			...getUtmsForProfile(),
			...(clickId && {
				click_id_type: clickId.type,
				click_id_value: clickId.value,
			}),
			...(document.referrer && { referrer: document.referrer }),
			landing_page: window.location.pathname + window.location.search,
		};

		fetch(TOUCHPOINT_URL, {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify(body),
			keepalive: true,
		}).catch(() => {});
	} catch {
		// Fire-and-forget — never throw to the caller.
	}
};

// Authenticated, idempotent re-stitch for the live session. Ties any of this
// browser's anonymous touches that arrived after the signup stitch (post-signup
// browsing, returning logged-in user, out-of-order/cross-subdomain) to the user.
// Once per session; a no-op once everything for this anonymous_id is linked.
export const stitchSession = (): void => {
	try {
		if (typeof window === 'undefined') return;
		const token = localStorage.getItem(LOCAL_STORAGE_KEY.TOKEN);
		if (!token) return; // not authenticated → nothing to stitch
		if (sessionStorage.getItem(STITCH_SESSION_KEY)) return; // once per session
		sessionStorage.setItem(STITCH_SESSION_KEY, '1');

		const affiliate = readAffiliateClick();
		const clickIdValues = [
			...(affiliate ? [affiliate.clickId] : []),
			...Object.values(getClickIds()),
		].filter(Boolean);

		fetch(STITCH_URL, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
				Authorization: `Bearer ${token}`,
			},
			body: JSON.stringify({
				anonymous_id: getAnonymousId(),
				...(clickIdValues.length > 0 && { click_ids: clickIdValues }),
			}),
			keepalive: true,
		}).catch(() => {});
	} catch {
		// Fire-and-forget — never throw to the caller.
	}
};
